import { useState } from 'react';
import { sfx } from '../audio/sfx.ts';

interface SettingsModalProps {
  open: boolean;
  onClose(): void;
  onFlipBoard?(): void;
  flipped?: boolean;
  onResign?(): void;
  onOfferDraw?(): void;
  canAct?: boolean;
}

export function SettingsModal({
  open,
  onClose,
  onFlipBoard,
  flipped = false,
  onResign,
  onOfferDraw,
  canAct = false,
}: SettingsModalProps) {
  const [soundEnabled, setSoundEnabled] = useState(sfx.isEnabled());
  const [volume, setVolume] = useState(Math.round(sfx.getVolume() * 100));
  const [skin, setSkin] = useState<'classic' | 'kychien'>(() => {
    if (typeof window === 'undefined') return 'classic';
    return (localStorage.getItem('xq_piece_skin') as 'classic' | 'kychien') || 'classic';
  });

  if (!open) return null;

  const handleToggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    sfx.setEnabled(next);
    if (next) sfx.playMove();
  };

  const handleChangeVolume = (newVol: number) => {
    setVolume(newVol);
    sfx.setVolume(newVol / 100);
    sfx.playMove();
  };

  const handleToggleSkin = () => {
    const next = skin === 'classic' ? 'kychien' : 'classic';
    setSkin(next);
    localStorage.setItem('xq_piece_skin', next);
    window.dispatchEvent(new Event('xq_skin_changed'));
    sfx.playMove();
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal settings-modal" role="dialog" aria-label="Cài đặt trận đấu">
        <div className="settings-header">
          <h2>⚙️ Cài đặt trận đấu</h2>
          <button className="settings-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="settings-content">
          {/* Sound toggle */}
          <div className="settings-row">
            <div className="setting-label">
              <strong>Âm thanh cờ (SFX)</strong>
              <span>Tiếng gõ quân, ăn quân, chiếu tướng</span>
            </div>
            <button
              type="button"
              className={`toggle-switch ${soundEnabled ? 'active' : ''}`}
              onClick={handleToggleSound}
              aria-label="Bật tắt âm thanh"
            >
              <span className="toggle-slider" />
            </button>
          </div>

          {/* Volume slider */}
          {soundEnabled && (
            <div className="settings-row volume-row">
              <div className="setting-label">
                <strong>Âm lượng</strong>
                <span>{volume}%</span>
              </div>
              <input
                type="range"
                min={10}
                max={100}
                step={5}
                value={volume}
                onChange={(e) => handleChangeVolume(Number(e.target.value))}
                className="volume-slider"
              />
            </div>
          )}

          {/* Flip board */}
          {onFlipBoard && (
            <div className="settings-row">
              <div className="setting-label">
                <strong>Đổi góc nhìn bàn cờ</strong>
                <span>{flipped ? 'Góc nhìn: Đen ở dưới' : 'Góc nhìn: Đỏ ở dưới'}</span>
              </div>
              <button
                type="button"
                className="action-pill-btn"
                onClick={() => {
                  onFlipBoard();
                  sfx.playMove();
                }}
              >
                🔄 Đảo bàn cờ
              </button>
            </div>
          )}

          {/* Skin selector */}
          <div className="settings-row">
            <div className="setting-label">
              <strong>Giao diện quân cờ</strong>
              <span>{skin === 'kychien' ? '🌟 Kỳ Chiến Hoàng Gia (Bản HD 512px)' : '🪵 Gỗ Mộc Cổ Điển (Mặc định - Siêu nhẹ, mượt)'}</span>
            </div>
            <button
              type="button"
              className="action-pill-btn"
              onClick={handleToggleSkin}
            >
              {skin === 'kychien' ? '🔄 Về Gỗ Cổ Điển' : '✨ Thử Kỳ Chiến HD'}
            </button>
          </div>

          {/* In-game actions (Resign / Draw) */}
          {canAct && (
            <div className="settings-match-actions">
              <h3>Hành động ván đấu</h3>
              <div className="action-buttons-group">
                {onOfferDraw && (
                  <button
                    type="button"
                    className="action-btn-draw"
                    onClick={() => {
                      onOfferDraw();
                      onClose();
                    }}
                  >
                    🤝 Cầu hòa
                  </button>
                )}
                {onResign && (
                  <button
                    type="button"
                    className="action-btn-resign"
                    onClick={() => {
                      onResign();
                      onClose();
                    }}
                  >
                    🏳️ Đầu hàng
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="settings-footer">
          <button className="primary" onClick={onClose}>
            Xong
          </button>
        </div>
      </div>
    </div>
  );
}
